import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import Button from '@welldev-group/rcl-button';
import { I18nText } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import Link from '@welldev-group/rcl-link';
import ConfirmationModal from '../../../shared/components/ConfirmationModal';
import PaginationWithTranslation from '../../../shared/components/Pagination/PaginationWithTranslation';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';
import WorkspaceTable from './WorkspaceTable';
import WorkSpaceModal from './WorkSpaceModal';
import CancelScheduledDowngradeModule from '../Billing/Subcription/CancelScheduledDowngradeModule';

const Workspaces = ({
  affiliateId,
  workspacesLimitReached,
  scheduledDowngrade,
  urls,
}) => {
  const [workspaces, setWorkspaces] = useState({});
  const [creatable, setCreatable] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPage] = useState(1);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceIdentifier, setWorkspaceIdentifier] = useState('');
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(
    false,
  );
  const [
    isWorkspaceCreationModalOpen,
    setIsWorkspaceCreationModalOpen,
  ] = useState(false);
  const [
    isWorkspaceDeletionModalOpen,
    setIsWorkspaceDeletionModalOpen,
  ] = useState(false);
  const ACTIVE_STATE = 'active';

  const isConfirmationModalOpen =
    isWorkspaceDeletionModalOpen || isWorkspaceCreationModalOpen;
  const confirmationModalSubheaderText = isWorkspaceCreationModalOpen ? (
    <I18nText id="workspace.create.confirmation_modal.success_subheader" />
  ) : (
    <I18nText id="workspace.delete.confirmation_modal.success_subheader" />
  );

  const fetchWorkspaces = async (page, params = null) => {
    const { data } = await httpClient.get(
      routes.workspace.fetch({
        q: params,
        affiliate_id: affiliateId,
        state: ACTIVE_STATE,
        page,
      }),
    );

    setWorkspaces(previousWorkspaces => {
      const newWorkSpaces = { ...previousWorkspaces };

      newWorkSpaces[affiliateId] = data.workspaces;

      return newWorkSpaces;
    });
    setCurrentPage(data.pagination.currentPage);
    setTotalPage(data.pagination.totalPages);
  };

  const updateWorkspace = async (id, workspaceName, affiliateIdOfWorkspace) => {
    const params = {
      workspace: {
        name: workspaceName,
        affiliate_id: affiliateIdOfWorkspace,
      },
    };
    const { data } = await httpClient.put(
      routes.workspace.update({ id, affiliate_id: affiliateIdOfWorkspace }),
      params,
    );
    return data;
  };
  const searchWorkspaces = debounce(500, async (firstPage, query) => {
    fetchWorkspaces(firstPage, query);
  });

  const onSearchParamsChange = value => {
    const firstPage = 1;
    const query = value;
    setSearchParam(query);
    searchWorkspaces(firstPage, query);
  };

  const createWorkspace = async () => {
    const params = {
      workspace: {
        name: workspaceName,
        identifier: workspaceIdentifier,
        affiliate_id: affiliateId,
      },
    };
    const { data } = await httpClient.post(
      routes.workspace.create({ affiliate_id: affiliateId }),
      params,
    );
    return data;
  };

  const deleteWorkspace = async (id, affiliateIdOfWorkspace) => {
    const { data } = await httpClient.delete(
      routes.workspace.delete({ id, affiliate_id: affiliateIdOfWorkspace }),
    );

    if (data.error === null) {
      setIsWorkspaceDeletionModalOpen(true);
    }

    return data;
  };

  const openConfirmationModalOfCreatingWorkspace = () => {
    setIsWorkspaceCreationModalOpen(true);
  };

  const resetConfirmationModalState = () => {
    if (isConfirmationModalOpen) {
      setIsWorkspaceCreationModalOpen(false);
      setIsWorkspaceDeletionModalOpen(false);
      onSearchParamsChange('');
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const modifyWorkspaces = (modifiedWorkspaces, affiliateIdOfWorkspace) => {
    setWorkspaces(previousWorkspaces => {
      const newWorkSpaces = { ...previousWorkspaces };

      newWorkSpaces[affiliateIdOfWorkspace] = modifiedWorkspaces;

      return newWorkSpaces;
    });
  };

  const toggleCreatable = value => {
    setCreatable(value);
  };

  return (
    <div className="workspace__container">
      <ConfirmationModal
        confirmationHeader={<I18nText id="shared.success" />}
        subHeader={confirmationModalSubheaderText}
        onTimeout={resetConfirmationModalState}
        withAction={false}
        timeout={3000}
        type="success"
        isModalOpen={isConfirmationModalOpen}
      />
      <div className="workspace__header col-12 col-bleed-x">
        <I18nText id="workspace.header" />
        <Button
          className="workspace__create-button"
          label={<I18nText id="workspace.create.button_label.new" />}
          version="v2"
          onClick={() => setShowCreateWorkspaceModal(true)}
          size="small"
          type="primary"
          disabled={workspacesLimitReached || Boolean(scheduledDowngrade)}
        />
        <WorkSpaceModal
          isVisible={showCreateWorkspaceModal}
          onModalClose={() => setShowCreateWorkspaceModal(false)}
          title={
            <I18nText id="affiliate_three_sixty.affiliate.workspaces.new.title" />
          }
          subheaderText={
            <I18nText id="affiliate_three_sixty.affiliate.workspaces.new.message" />
          }
          userAffiliateId={affiliateId}
          action={createWorkspace}
          fetchWorkspaces={fetchWorkspaces}
          openConfirmationModalOfCreatingWorkspace={
            openConfirmationModalOfCreatingWorkspace
          }
          workspaceName={workspaceName}
          setWorkspaceName={setWorkspaceName}
          workspaceIdentifier={workspaceIdentifier}
          setWorkspaceIdentifier={setWorkspaceIdentifier}
          buttonText={<I18nText id="workspace.create.button_label.create" />}
        />
      </div>
      {scheduledDowngrade ? (
        <div className="col-12 col-bleed-x">
          <CancelScheduledDowngradeModule
            affiliateId={affiliateId}
            scheduledDowngrade={scheduledDowngrade}
            redirectTo={urls.workspaces}
          />
        </div>
      ) : null}
      {workspacesLimitReached ? (
        <div className="col-12 col-bleed-x">
          <Alert
            className="workspace__limit-alert"
            isVisible={workspacesLimitReached}
            type="warning-light"
            hideClose={true}
          >
            <div className="workspace__limit-alert-content">
              <div className="workspace__limit-alert-content-text">
                {I18n.t('components.workspace.workspace_limit_alert_text')}
              </div>
              <Link
                href={urls.plans}
                className="workspace__limit-alert-content-button"
                size="small"
                type="button"
                modifier="default"
              >
                {I18n.t('components.billing.plan.link_view')}
              </Link>
            </div>
          </Alert>
        </div>
      ) : null}
      <div className="workspace__search-input">
        <Input
          value={searchParam}
          onChange={onSearchParamsChange}
          preIcon={<Icon name="search" />}
          placeholder={I18n.t('components.support.search')}
        />
      </div>
      <WorkspaceTable
        modifyWorkspaces={modifyWorkspaces}
        affiliateId={affiliateId}
        workspaces={workspaces[affiliateId] || []}
        fetchWorkspaces={fetchWorkspaces}
        updateWorkspace={updateWorkspace}
        creatable={creatable}
        toggleCreatable={toggleCreatable}
        createWorkspace={createWorkspace}
        deleteWorkspace={deleteWorkspace}
        openConfirmationModalOfCreatingWorkspace={
          openConfirmationModalOfCreatingWorkspace
        }
        rowActionDisabled={Boolean(scheduledDowngrade)}
      />
      <PaginationWithTranslation
        currentPage={currentPage}
        totalPages={totalPages}
        onPaginationClick={value => {
          setCurrentPage(value);
          fetchWorkspaces(value, searchParam);
        }}
      />
    </div>
  );
};

Workspaces.defaultProps = {
  urls: {
    plans: '',
    workspaces: '',
  },
  scheduledDowngrade: null,
};

Workspaces.propTypes = {
  affiliateId: PropTypes.number.isRequired,
  workspacesLimitReached: PropTypes.bool.isRequired,
  scheduledDowngrade: PropTypes.shape({
    id: PropTypes.number,
    plan: PropTypes.shape({}),
    downgradedFeatures: {
      lessWorkspaces: PropTypes.number,
      lessChannels: PropTypes.number,
      customDomainNotSupported: PropTypes.bool,
      lessPhoneSupport: PropTypes.bool,
    },
    downgradeAt: PropTypes.string,
  }),
  urls: PropTypes.shape({
    plans: PropTypes.string,
    workspaces: PropTypes.string,
  }),
};

export default Workspaces;
